/**
 * Battle factory — hydrate a Battle from two strategic-map Armies (v0M3.4).
 *
 * Called by `zoc-detection.ts` when two Armies of different teams enter
 * ZoC overlap on the strategic map per `MECHANICS.md` §3.2. Snapshots
 * both Armies' cohortIds into `CohortOnField` instances using their
 * campaign Cohort roster + the corresponding `CohortSpec` stats from
 * `loadCohorts()`.
 *
 * Design decisions embedded here:
 *  - **Bilateral only at v0M3.4** — v1 design supports N-sided
 *    engagements per §2.7.1, but the v0M3.4 first pass ships bilateral.
 *  - **Side placement**: the team with the LOWER Faction id deploys on
 *    the **south** half of the battlescape (higher y coords, rows 15-19),
 *    the other team deploys on the **north** half (rows 0-4). Matches
 *    `BATTLES.md` §2.7.8.4.a: player-side = south facing north; enemy =
 *    north facing south. Which team is "player-side" is decided
 *    deterministically by Faction id (lower = south) since v0M3.4 has
 *    no player-camera concept.
 *  - **Auto-layout**: cohorts spread horizontally around x=10 with 1-cell
 *    spacing. `field_cap` per `BATTLES.md` §2.7.8.11 (20 cohorts per side)
 *    means we support up to 20 per side.
 *  - **Fresh figures**: hydrated CohortOnField starts with the campaign
 *    Cohort's `aliveFigureCount` figures at full max health, wounded=false,
 *    killed=false. Casualties between battles are stored in
 *    `Cohort.aliveFigureCount`, not per-figure state.
 *  - **Battle PRNG seed**: derived from `state.seed XOR battle id` for
 *    deterministic replay per `MECHANICS.md` §1.1.
 */

import { loadCohorts } from '../data/cohorts';
import type { CohortSpec } from '../data/cohorts';
import { getDialNumber, loadDials } from '../data/dials';

// MFix4.2: phase-1 (deployment/setup) tick budget — matches apply-commands
// PHASE1_TICKS. Set at battle creation so setup runs autonomously whether
// or not the player opens the battle-command modal.
const FACTORY_DIALS = loadDials();
const FACTORY_PHASE1_TIMER_SEC = getDialNumber(FACTORY_DIALS, 'battle_phase1_timer_sec');
const FACTORY_PHASE1_TICKS = Math.round(FACTORY_PHASE1_TIMER_SEC * 5); // 5 = ticks/wall-second at 1×
import type {
  Battle,
  CohortOnField,
  CohortStats,
  CommanderOrder,
  Facing,
  Figure,
  FacingState,
  FormationTemplate,
  Side,
  Square,
  StandingOrder,
} from './battle-state';
import {
  BATTLESCAPE_CELL_COUNT,
  BATTLESCAPE_HEIGHT,
  BATTLESCAPE_WIDTH,
  COHORT_SIZE,
  SIEGE_COHORT_SIZE,
  toBattleId,
} from './battle-state';
import { createRng } from './rng';
import { createCommander, toCommanderId } from './state';
import type { Army, Settlement, Cohort, CohortId, CommanderId, GameState } from './state';
import type { SimContext } from './sim-context';
import {
  generateBattleTerrain,
  generateSiegeBattleTerrain,
  isPassable,
  SIEGE_FRONT_WALL_ROW,
  strategicCueToBattlescape,
  terrainTypeAt,
  WALL_HP_INITIAL,
} from './battle-terrain';
import { loadTerrainTemplates } from '../data/battle-templates';
import { applyTerrainTemplate, pickTerrainTemplate, pickTerrainTemplatePair, stitchAndApplyHalves } from './battle-template-apply';
import { loadFormationTemplates, slotsByTag, cohortPrimaryTag } from '../data/formation-templates';
import type { FormationSlot as FormationSlotFromXlsx, FormationSlotTag } from '../data/formation-templates';
// v0M13.1 (2026-07-30) — battle-entry gate. Ineligible cohorts stay on
// their parent Army roster and are NEVER placed on the battlescape;
// they don't appear in Battle.fieldCohorts, take no damage, aren't
// touched by writeback/destroy/disband/retreat — they survive intact
// regardless of battle outcome per BATTLES.md §2.7.2.a.
import { isCohortEligibleForBattle } from './battle-entry-gate';

// v0M4.3.3 (2026-07-23) — Terrain-template library, loaded once at
// module init. Used by both siege and field battle creation to override
// procedural terrain when a matching xlsx template exists.
const TERRAIN_TEMPLATES = loadTerrainTemplates();
// v0M10.7 (2026-07-24) — Settlement Walls opt-in gate. Consulted by
// createSiegeBattleFromArmies to choose walled vs unwalled template.
const WALLS_STRUCTURE_NAME = 'Settlement Walls';

/**
 * True iff the Settlement's province has the `Settlement Walls`
 * structure built. Consumers use this to route siege-battle terrain
 * templating (walled vs unwalled). Returns false for owner-less
 * settlements too (province.ownerFactionId === null) — no walls until
 * someone owns + builds them.
 */
function settlementHasWallsStructure(state: GameState, settlement: Settlement): boolean {
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return false;
  return province.structures.some((s) => s.name === WALLS_STRUCTURE_NAME);
}
// v0M4.3.4 (2026-07-23) — Formation-template library, loaded once.
// Consumed by applyFormation to place cohorts per tag+priority slots.
const FORMATION_TEMPLATES = loadFormationTemplates();

const COHORTS = loadCohorts();

/**
 * Create a new Battle from two enemy Armies. Returns the fully-hydrated
 * Battle ready to register in `state.battles`. Bumps `state.nextBattleId`.
 *
 * Both armies get their `currentBattleId` set to the new battle's id +
 * their `plannedPath`/`movementGoal` cleared — the "engagement lock"
 * invariant is atomic here.
 */
export function createBattleFromArmies(
  state: GameState,
  armyA: Army,
  armyB: Army,
  context?: SimContext,
): Battle {
  // Deterministic side order — lower faction id → side 0 (south half).
  const [southArmy, northArmy] =
    armyA.ownerFactionId <= armyB.ownerFactionId ? [armyA, armyB] : [armyB, armyA];

  const battleId = toBattleId(state.nextBattleId++);
  const seed = (state.seed ^ battleId) >>> 0;
  const battleRng = createRng(seed);

  // v0M4.0j.1: generate battlefield terrain from strategic-map cue + battle
  // RNG (rolled before hydrateSide so cohort placement can shift off any
  // impassable cell). When no context is supplied (test fixtures), fall
  // back to an all-Grass grid so existing tests aren't perturbed.
  const { terrain, features } = context !== undefined
    ? generateTerrainFromContext(context, southArmy, northArmy, battleRng)
    : { terrain: emptyGrassTerrain(), features: [] };

  const side0 = hydrateSide(state, southArmy, 0, 'N', terrain);
  const side1 = hydrateSide(state, northArmy, 1, 'S', terrain);

  const battle: Battle = {
    id: battleId,
    armyIds: [southArmy.id, northArmy.id],
    autoAI: true, // v0M3.8a — ZoC-created battles auto-target cohorts.
    // MFix4.2 — deployment/setup phase runs autonomously from battle
    // creation. Set to `tick + PHASE1_TICKS` here so every battle enters
    // Setup for the same window regardless of whether a player opens
    // the modal. Enter/exit-battle-command no longer touches this.
    phase1EndTick: state.tick + FACTORY_PHASE1_TICKS,
    lastDamageRound: -1, // v0M3.5+fix2 — stall-watchdog seed.
    roundTickAccumulator: 0, // v0M4.0f — round-pacing accumulator.
    round: 0,
    // v0M4.1.3 — sub-phase state; battle starts in Movement at the tick
    // the battle was created. When player takes command via
    // enter_battle_command, phase-1 (setup) freezes advancement until
    // phase1EndTick elapses; on release, phaseStartTick is refreshed
    // in battles.ts so timing starts cleanly from the resume tick.
    currentPhase: 'movement',
    phaseStartTick: state.tick,
    rng: battleRng,
    sides: [side0, side1],
    terrain,
    terrainFeatures: features,
    log: [], // v0M4.1.1 — battle-scoped combat log.
    // v0M4.2.3 — field battle (not a siege). Siege battles land at
    // v0M4.2.5 via `createSiegeBattleFromArmies`.
    siegeSettlement: null,
    wallHp: new Map(),
    result: null,
  };

  // Engagement lock — armies are now in this battle.
  southArmy.currentBattleId = battleId;
  southArmy.plannedPath = null;
  southArmy.movementGoal = null;
  northArmy.currentBattleId = battleId;
  northArmy.plannedPath = null;
  northArmy.movementGoal = null;

  return battle;
}

/**
 * v0M4.2.3 — Create a city-siege battle per BATTLES.md §3.5. Called by
 * the assault trigger (v0M4.2.5) when an attacker chooses to assault a
 * besieged city. The layout uses `generateSiegeBattleTerrain` (U-shape
 * walls, 3 gates) and deploys defender cohorts (garrison + optional
 * defender field army) inside the walls, attacker cohorts on the far
 * edge.
 *
 * Convention:
 *   - sideIndex 0 = **defender** — occupies the walled area (rows 13-18).
 *     Deploys garrison cohorts from `city.garrisonCohortIds`, plus any
 *     defender field army if present. Facing 'N' (looking at attackers).
 *   - sideIndex 1 = **attacker** — occupies the far edge (rows 0-4).
 *     Deploys the sieging army's cohorts. Facing 'S' (looking at walls).
 *
 * Commander:
 *   - Defender: if `defenderArmy` is provided, use its commander. If
 *     garrison-only, mint a synthetic commander for the battle (level 1,
 *     0 XP; kept alive after battle for possible re-use — the garrison
 *     survives multiple assault attempts and its commander persists).
 *   - Attacker: uses `attackerArmy.commanderId`.
 *
 * Battle.siegeSettlement is set to `city.id` — downstream code (`sieges.ts`,
 * `battle-round.ts` Catapult targeting v0M4.2.4, capture handling
 * v0M4.2.5) branches on this. Battle.wallHp is seeded with
 * `WALL_HP_INITIAL` for each wall cell returned by the terrain
 * generator.
 *
 * Engagement lock: sets attackerArmy.currentBattleId and clears
 * plannedPath / movementGoal. If defenderArmy is provided, same lock
 * applied.
 */
export function createSiegeBattleFromArmies(
  state: GameState,
  attackerArmy: Army,
  city: Settlement,
  defenderArmy: Army | null,
  context: SimContext,
): Battle {
  const battleId = toBattleId(state.nextBattleId++);
  const seed = (state.seed ^ battleId) >>> 0;
  const battleRng = createRng(seed);

  // v0M10.7 (2026-07-24) — Settlement Walls opt-in model. Retires the
  // v0M4.3.3 "walls on all cities" default. Walls now appear only if
  // the Settlement's province has the `Settlement Walls` structure
  // built. Rationale: matches Michael's session-29 structure roster
  // where Settlement Walls is a per-Settlement decision. Scenario boot
  // seeds no walls anywhere; players + AI opt in by building the
  // structure. Category dispatch:
  //   - Walls built    → SettlementBattlefield_Walled (category=settlement_walled)
  //   - No walls       → SettlementBattlefield_NoWall (category=settlement_nowalls)
  //   - Template gone  → procedural U-shape fallback (dev-only edge case)
  // v0M13.2c (2026-07-30) — category names renamed city_* → settlement_*
  // for consistency with the SettlementBattlefield_* sheet naming that
  // already landed at v0M10.3. The xlsx sheet-name path in
  // scripts/convert-xlsx.ts::categorizeTerrainSheet continues to accept
  // both SettlementBattlefield_* + legacy CityBattlefield_* sheet names.
  const hasWalls = settlementHasWallsStructure(state, city);
  const templateCategory = hasWalls ? 'settlement_walled' : 'settlement_nowalls';
  const pickedTemplate = pickTerrainTemplate(TERRAIN_TEMPLATES, templateCategory, battleRng);
  let terrain: Uint8Array;
  let features: import('./battle-state').TerrainFeature[];
  let wallCells: readonly number[];
  if (pickedTemplate) {
    const applied = applyTerrainTemplate(pickedTemplate);
    terrain = applied.terrain;
    features = applied.features;
    wallCells = applied.wallCells;
  } else {
    // Procedural fallback (pre-v0M4.3.3 path). When falling back for a
    // no-walls Settlement, the U-shape generator's walls are still
    // emitted — this is imperfect but the fallback is dev-only (would
    // only fire if Michael removes the SettlementBattlefield_* xlsx
    // tabs). Not worth a second procedural generator today.
    const cue = sampleStrategicCueAt(context, city.cell.x, city.cell.y);
    const gen = generateSiegeBattleTerrain(battleRng, {
      cue,
      hasNearbyRiver: false,
      hasNearbyForest: false,
      hasNearbyHills: false,
      nearFriendlyCity: false,
    });
    terrain = gen.terrain;
    features = gen.features;
    wallCells = gen.wallCells;
  }

  // Defender cohorts = city garrison + optional defender field army roster.
  const defenderCohortIds: CohortId[] = [...city.garrisonCohortIds];
  if (defenderArmy) {
    for (const cid of defenderArmy.cohortIds) defenderCohortIds.push(cid);
  }
  // Defender commander: reuse field-army commander if present; else mint
  // a fresh Default commander for the garrison-only defense.
  let defenderCommanderId: CommanderId;
  if (defenderArmy) {
    defenderCommanderId = defenderArmy.commanderId;
  } else {
    defenderCommanderId = toCommanderId(state.nextCommanderId++);
    state.commanders.set(defenderCommanderId, createCommander(defenderCommanderId));
  }

  // Hydrate sides. Defender fills rows 13-18 (interior of walls), attacker
  // fills rows 0-4 (opposite edge).
  const side0 = hydrateSiegeSide(
    state,
    defenderCohortIds,
    defenderCommanderId,
    /* sideIndex */ 0,
    /* facing */ 'N',
    terrain,
    /* rowStart */ SIEGE_FRONT_WALL_ROW + 1, // row 13
    /* rowEnd   */ BATTLESCAPE_HEIGHT - 1,   // row 19 (exclusive of the rear-edge row)
  );
  const side1 = hydrateSiegeSide(
    state,
    attackerArmy.cohortIds,
    attackerArmy.commanderId,
    /* sideIndex */ 1,
    /* facing */ 'S',
    terrain,
    /* rowStart */ 0,
    /* rowEnd   */ 5,
  );

  // Seed wallHp for every wall cell returned by the generator.
  const wallHp = new Map<number, number>();
  for (const idx of wallCells) wallHp.set(idx, WALL_HP_INITIAL);

  const battle: Battle = {
    id: battleId,
    // For siege battles the "armyIds" pair is [attackerArmyId, defenderArmyId
    // or attackerArmyId again if no defender army]. Downstream code will use
    // Battle.siegeSettlement to branch on siege-vs-field semantics; the armyIds
    // duplication for garrison-only defense is a deliberate placeholder
    // pending a schema amendment (v0M4.2.5 candidate) that admits garrison-
    // only participation without a placeholder Army id.
    armyIds: [attackerArmy.id, defenderArmy ? defenderArmy.id : attackerArmy.id],
    autoAI: true,
    phase1EndTick: null,
    lastDamageRound: -1,
    roundTickAccumulator: 0,
    round: 0,
    currentPhase: 'movement',
    phaseStartTick: state.tick,
    rng: battleRng,
    sides: [side0, side1],
    terrain,
    terrainFeatures: features,
    log: [],
    siegeSettlement: city.id,
    wallHp,
    result: null,
  };

  // Engagement lock — armies enter the battle.
  attackerArmy.currentBattleId = battleId;
  attackerArmy.plannedPath = null;
  attackerArmy.movementGoal = null;
  if (defenderArmy) {
    defenderArmy.currentBattleId = battleId;
    defenderArmy.plannedPath = null;
    defenderArmy.movementGoal = null;
  }

  return battle;
}

/**
 * v0M7.4d+siege (2026-07-26) — Fort assault battle factory.
 *
 * Peer of `createSiegeBattleFromArmies` but targets a WorldStructure
 * of kind='fort' instead of a Settlement. Semantics per MAP.md §9.2
 * + session-33 scope decision:
 *   - Battlemap template: category 'fort_walled' (Michael-authored
 *     FortAssault* rows in BattleMap.xlsx). Falls back to the same
 *     procedural U-shape generator as settlement siege if no template
 *     matches — dev-only edge case (Michael's expected shipping
 *     content includes fort templates).
 *   - Defender cohorts: Fort's garrisonCohortIds only. No mid-siege
 *     defender-field-army support at v1 (mirrors settlement §3.5 v1
 *     scope).
 *   - Defender commander: fresh Default commander minted since Fort
 *     garrisons have no persistent commander.
 *   - Wall HP seeded from template's wallCells (same as settlement
 *     walls; wall breach mechanic already generic).
 *   - Battle carries `siegeFortId` set to the target Fort's id;
 *     battle-conclusion consumes this to flip ownership + destroy
 *     garrison on attacker victory.
 */
export function createFortAssaultBattleFromArmies(
  state: GameState,
  attackerArmy: Army,
  fort: import('./state').WorldStructure,
  context: SimContext,
): Battle {
  const battleId = toBattleId(state.nextBattleId++);
  const seed = (state.seed ^ battleId) >>> 0;
  const battleRng = createRng(seed);

  // Template dispatch — category 'fort_walled' (Michael-authored).
  // Falls back to procedural U-shape when no template matches so
  // Fort assaults ship even before the xlsx rows land.
  const pickedTemplate = pickTerrainTemplate(TERRAIN_TEMPLATES, 'fort_walled', battleRng);
  let terrain: Uint8Array;
  let features: import('./battle-state').TerrainFeature[];
  let wallCells: readonly number[];
  if (pickedTemplate) {
    const applied = applyTerrainTemplate(pickedTemplate);
    terrain = applied.terrain;
    features = applied.features;
    wallCells = applied.wallCells;
  } else {
    // Procedural fallback — same U-shape generator as settlements but
    // seeded from the Fort's own strategic cell.
    const cue = sampleStrategicCueAt(context, fort.cell.x, fort.cell.y);
    const gen = generateSiegeBattleTerrain(battleRng, {
      cue,
      hasNearbyRiver: false,
      hasNearbyForest: false,
      hasNearbyHills: false,
      nearFriendlyCity: false,
    });
    terrain = gen.terrain;
    features = gen.features;
    wallCells = gen.wallCells;
  }

  // Defender cohorts = Fort garrison. Fresh Default commander for the
  // garrison side (garrisons have no persistent commander).
  const defenderCohortIds: CohortId[] = [...(fort.garrisonCohortIds ?? [])];
  const defenderCommanderId = toCommanderId(state.nextCommanderId++);
  state.commanders.set(defenderCommanderId, createCommander(defenderCommanderId));

  const side0 = hydrateSiegeSide(
    state,
    defenderCohortIds,
    defenderCommanderId,
    /* sideIndex */ 0,
    /* facing */ 'N',
    terrain,
    /* rowStart */ SIEGE_FRONT_WALL_ROW + 1,
    /* rowEnd */ BATTLESCAPE_HEIGHT - 1,
  );
  const side1 = hydrateSiegeSide(
    state,
    attackerArmy.cohortIds,
    attackerArmy.commanderId,
    /* sideIndex */ 1,
    /* facing */ 'S',
    terrain,
    /* rowStart */ 0,
    /* rowEnd */ 5,
  );

  const wallHp = new Map<number, number>();
  for (const idx of wallCells) wallHp.set(idx, WALL_HP_INITIAL);

  const battle: Battle = {
    id: battleId,
    // Fort assaults have no defender field army; use the attacker id
    // twice as a placeholder (same convention as garrison-only siege
    // per createSiegeBattleFromArmies).
    armyIds: [attackerArmy.id, attackerArmy.id],
    autoAI: true,
    phase1EndTick: null,
    lastDamageRound: -1,
    roundTickAccumulator: 0,
    round: 0,
    currentPhase: 'movement',
    phaseStartTick: state.tick,
    rng: battleRng,
    sides: [side0, side1],
    terrain,
    terrainFeatures: features,
    log: [],
    siegeSettlement: null,
    siegeFortId: fort.id,
    wallHp,
    result: null,
  };

  attackerArmy.currentBattleId = battleId;
  attackerArmy.plannedPath = null;
  attackerArmy.movementGoal = null;

  return battle;
}

/**
 * v0M4.2.3 — Sample the strategic-map terrain id at a cell and convert it
 * to a battlescape cue. Used by `createSiegeBattleFromArmies` to seed the
 * attacker-half terrain scatter. Falls back to Grass on out-of-bounds.
 */
function sampleStrategicCueAt(context: SimContext, x: number, y: number): import('./battle-state').TerrainType {
  const w = context.terrain.width;
  const h = context.terrain.height;
  const cx = Math.max(0, Math.min(w - 1, x));
  const cy = Math.max(0, Math.min(h - 1, y));
  const strategicId = context.terrain.terrain[cy * w + cx] ?? 0;
  return strategicCueToBattlescape(strategicId);
}

/**
 * v0M4.2.3 — Siege-side hydration. Same shape as `hydrateSide` but takes
 * a flat cohort-id list + commanderId + explicit row range (siege
 * deployment zones differ from field-battle rows 0-4 / 15-19).
 * Cohorts are placed via `layoutSquares(rowStart, rowEnd)` and shifted
 * off walls / impassable cells by `resolveTerrainCollision`.
 */
function hydrateSiegeSide(
  state: GameState,
  cohortIds: readonly CohortId[],
  commanderId: CommanderId,
  sideIndex: 0 | 1,
  facing: Facing,
  terrain: Uint8Array,
  rowStart: number,
  rowEnd: number,
): Side {
  const cohorts = new Map<CohortId, CohortOnField>();
  // v0M13.1 (2026-07-30) — filter through the entry gate before layout.
  // Ineligible cohorts skip placement entirely: they don't consume a
  // deployment slot, don't get a CohortOnField, and remain on Army.cohortIds
  // untouched so writeback/destroy/disband/retreat all bypass them.
  // Garrison cohorts (armyId === null) also go through the gate — an
  // exhausted garrison stays in reserve on the Settlement's roster.
  const eligibleIds: CohortId[] = [];
  for (const cohortId of cohortIds) {
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue;
    if (!isCohortEligibleForBattle(cohort)) continue;
    eligibleIds.push(cohortId);
  }
  const placements = layoutSquares(eligibleIds.length, rowStart, rowEnd);
  const occupied = new Set<number>();
  for (let i = 0; i < eligibleIds.length; i++) {
    const cohortId = eligibleIds[i]!;
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue;
    const spec = COHORTS[cohort.type];
    if (!spec) continue;
    const preferred = placements[i] ?? { x: 0, y: rowStart };
    const resolved = resolveTerrainCollision(preferred, occupied, terrain);
    occupied.add(resolved.y * BATTLESCAPE_WIDTH + resolved.x);
    cohorts.set(cohortId, buildCohortOnField(cohort, spec, resolved, facing));
  }
  return { sideIndex, cohorts, commanderId };
}

// ---------------------------------------------------------------------------
// Side hydration — build a Side with CohortOnField per cohort in the army roster
// ---------------------------------------------------------------------------

function hydrateSide(
  state: GameState,
  army: Army,
  sideIndex: 0 | 1,
  facing: Facing,
  terrain: Uint8Array,
): Side {
  const cohorts = new Map<CohortId, CohortOnField>();

  // Deployment squares: side 0 = rows 15-19 (south half); side 1 = rows 0-4 (north).
  const rowStart = sideIndex === 0 ? BATTLESCAPE_HEIGHT - 5 : 0;
  const rowEnd = rowStart + 5;

  // v0M13.1 (2026-07-30) — entry gate. Filter first, then lay out only
  // the eligible cohorts. Ineligible cohorts stay on Army.cohortIds and
  // never enter this Side's cohort map — writeback/destroy/disband bypass
  // them entirely, so they survive intact regardless of battle outcome.
  const eligibleIds: CohortId[] = [];
  for (const cohortId of army.cohortIds) {
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue;
    if (!isCohortEligibleForBattle(cohort)) continue;
    eligibleIds.push(cohortId);
  }
  const placements = layoutSquares(eligibleIds.length, rowStart, rowEnd);

  // v0M4.0j.1: shift any placement that lands on an impassable cell via
  // deterministic outward spiral per BATTLES.md §2.8.6.4. Occupied set
  // tracks cohorts already placed this call so we never stack two.
  const occupied = new Set<number>();
  for (let i = 0; i < eligibleIds.length; i++) {
    const cohortId = eligibleIds[i]!;
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue; // Dangling id — skip.
    const spec = COHORTS[cohort.type];
    if (!spec) continue; // Unknown type — skip (Balance.xlsx author error).
    const preferred = placements[i] ?? { x: 0, y: rowStart };
    const resolved = resolveTerrainCollision(preferred, occupied, terrain);
    occupied.add(resolved.y * BATTLESCAPE_WIDTH + resolved.x);
    cohorts.set(cohortId, buildCohortOnField(cohort, spec, resolved, facing));
  }

  return {
    sideIndex,
    cohorts,
    commanderId: army.commanderId,
  };
}

/**
 * v0M4.0j.1 — Deterministic outward-spiral search for the nearest passable
 * unoccupied cell per BATTLES.md §2.8.6.4. If the preferred cell is
 * passable + free, returns it as-is. Otherwise walks radius r = 1..5,
 * visiting neighbours in fixed order (N → NE → E → SE → S → SW → W → NW).
 * If radius 5 still fails, falls back to placing on the preferred cell
 * regardless of passability (placeholder failure mode per §2.8.6.4).
 */
function resolveTerrainCollision(
  preferred: Square,
  occupied: Set<number>,
  terrain: Uint8Array,
): Square {
  const OFFSETS: readonly (readonly [number, number])[] = [
    [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1],
  ];
  const cellFree = (x: number, y: number): boolean => {
    if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) return false;
    if (occupied.has(y * BATTLESCAPE_WIDTH + x)) return false;
    return isPassable(terrainTypeAt(terrain, x, y));
  };
  if (cellFree(preferred.x, preferred.y)) return preferred;
  for (let r = 1; r <= 5; r++) {
    for (const [dx, dy] of OFFSETS) {
      const x = preferred.x + dx * r;
      const y = preferred.y + dy * r;
      if (cellFree(x, y)) return { x, y };
    }
  }
  return preferred; // Fallback per §2.8.6.4 — cohort deploys on impassable.
}

/**
 * v0M4.0j.1 — Sample strategic-map cue at the ZoC midpoint + roll feature
 * flags from nearby-strategic-cell scan, then call `generateBattleTerrain`.
 * Extracted so `createBattleFromArmies` reads cleanly.
 */
function generateTerrainFromContext(
  context: SimContext,
  armyA: Army,
  armyB: Army,
  rng: import('./rng').RngState,
): { terrain: Uint8Array; features: import('./battle-state').TerrainFeature[] } {
  const midX = Math.floor((armyA.position.x + armyB.position.x) / 2);
  const midY = Math.floor((armyA.position.y + armyB.position.y) / 2);
  const w = context.terrain.width;
  const h = context.terrain.height;
  const clampedX = Math.max(0, Math.min(w - 1, midX));
  const clampedY = Math.max(0, Math.min(h - 1, midY));
  const strategicId = context.terrain.terrain[clampedY * w + clampedX] ?? 0;
  const cue = strategicCueToBattlescape(strategicId);
  // Nearby-cell scan for feature flags — 5×5 window around the midpoint.
  let hasNearbyRiver = false;
  let hasNearbyForest = false;
  let hasNearbyHills = false;
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      const nx = clampedX + dx;
      const ny = clampedY + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
      const id = context.terrain.terrain[ny * w + nx]!;
      if (id === 7) hasNearbyRiver = true;      // Lake — proxy for river presence
      if (id === 2) hasNearbyForest = true;     // Forest
      if (id === 3) hasNearbyHills = true;      // Hills
    }
  }
  // Fortification: `context.provinceMap` (optional at v0M3.12) tells us
  // which strategic cell belongs to which province — we don't currently
  // have owner-team info without state, so v0M4.0j.1 conservatively skips
  // the Fortification cue. Playtest-open per §2.8.6.8.
  const nearFriendlyCity = false;
  const base = generateBattleTerrain(rng, {
    cue,
    hasNearbyRiver,
    hasNearbyForest,
    hasNearbyHills,
    nearFriendlyCity,
  });
  // v0M4.3.3 (2026-07-23): if the battle spawned near a river feature
  // AND the xlsx template library has RiverCrossing templates, RNG-pick
  // one and overlay it on top of the procedural base. Cells that hold
  // `X` in the template preserve the base (so hills / forest scatter
  // outside the river band still shows through).
  // v0M13.2b (2026-07-30) — river is now a half-template category per
  // BATTLES.md §2.8.6-B. Pick TWO halves independently and stitch (mirror
  // top, unmirrored bottom). Base terrain preserved for cells with `X`
  // in either half.
  if (hasNearbyRiver) {
    const pair = pickTerrainTemplatePair(TERRAIN_TEMPLATES, 'river', rng);
    if (pair) {
      const applied = stitchAndApplyHalves(pair.top, pair.bottom, base.terrain);
      return {
        terrain: applied.terrain,
        features: [...base.features, ...applied.features],
      };
    }
  }
  return base;
}

/**
 * v0M4.0j.1 — All-Grass 20×20 terrain grid used by test fixtures that
 * call `createBattleFromArmies` without a SimContext. Consistent with
 * `TERRAIN_TYPE_ORDER[0] === 'Plains'`, so a zero-filled Uint8Array is
 * already "all Grass" — but this named factory documents the intent.
 */
function emptyGrassTerrain(): Uint8Array {
  return new Uint8Array(BATTLESCAPE_CELL_COUNT);
}

/**
 * Compute deployment squares for `n` cohorts within rows [rowStart, rowEnd).
 * Fills row-by-row starting at rowStart. Each row centres its occupants
 * around x = BATTLESCAPE_WIDTH / 2.
 */
function layoutSquares(n: number, rowStart: number, rowEnd: number): Square[] {
  if (n === 0) return [];
  const centreX = Math.floor(BATTLESCAPE_WIDTH / 2);
  const perRow = Math.min(n, BATTLESCAPE_WIDTH);
  const halfRow = Math.floor(perRow / 2);
  const out: Square[] = [];

  let placedThisRow = 0;
  let currentRow = rowStart;
  for (let i = 0; i < n; i++) {
    if (currentRow >= rowEnd) break;
    const offset = placedThisRow - halfRow;
    out.push({ x: centreX + offset, y: currentRow });
    placedThisRow++;
    if (placedThisRow >= perRow) {
      placedThisRow = 0;
      currentRow++;
    }
  }
  return out;
}

function buildCohortOnField(
  cohort: Cohort,
  spec: CohortSpec,
  square: Square,
  facing: Facing,
): CohortOnField {
  // Session-27 (2026-07-24): figure count is now xlsx-authoritative
  // per cohort (`figureCount` col N). Falls back to the Siege-tag branch
  // only when the spec somehow lacks a figureCount, matching the
  // pre-session-27 hardcoded behaviour.
  const totalSlots = spec.figureCount > 0
    ? spec.figureCount
    : (spec.tags.includes('Siege') ? SIEGE_COHORT_SIZE : COHORT_SIZE);
  const figs = Math.max(0, Math.min(cohort.aliveFigureCount, totalSlots));
  const figures: Figure[] = [];
  for (let i = 0; i < figs; i++) {
    figures.push({ health: spec.maxHealthPerFigure, wounded: false, killed: false });
  }

  const stats: CohortStats = {
    attack: spec.attack,
    defence: spec.defence,
    // v0M9.13 (2026-07-24) — apply the per-cohort armor bonus baked in at
    // recruit time (Armorers Guild +5 at v1). Undefined for pre-v0M9.13
    // fixtures → treated as 0 by the `?? 0` coalesce.
    armor: spec.armor + (cohort.armorBonus ?? 0),
    initiative: spec.initiative,
    chargeBonus: spec.chargeBonus,
    missileAttack: spec.missileAttack,
    missileRange: spec.missileRange,
    maxHealthPerFigure: spec.maxHealthPerFigure,
    // v0M4.0j.2 — moveSpeedCombat carried from CohortSpec.
    // Session-27 (2026-07-24): xlsx now authoritative; retain a `?? 1`
    // defensive fallback in case a test-fixture spec omits the field.
    moveSpeedCombat: spec.moveSpeedCombat ?? 1,
    // Session-27b (2026-07-24): frontWidth + figureCount carried per cohort.
    // Renderer + healthyFrontRowCount now read these instead of the
    // module-level COHORT_WIDTH / COHORT_SIZE constants.
    frontWidth: spec.frontWidth ?? 12,
    figureCount: spec.figureCount ?? totalSlots,
  };

  const facingState: FacingState = { direction: facing, turnedThisRound: false };

  return {
    id: cohort.id,
    armyId: cohort.armyId,
    factionId: cohort.factionId,
    type: cohort.type,
    tags: [...spec.tags],
    stats,
    position: square,
    facing: facingState,
    // v0M4.0b.3: tag-conditional default per §2.8.1.
    // - Ranged-only cohorts: fire-at-will (default ranged posture).
    // - Everything else (Melee-only, dual-tag Melee+Ranged, Siege):
    //   advance-and-attack (default melee posture; dual-tag defaults
    //   to melee-first — player can flip via UI).
    standingOrder: spec.tags.includes('Ranged') && !spec.tags.includes('Melee')
      ? 'fire-at-will'
      : 'advance-and-attack',
    // v0M3.11: seed CohortOnField stamina + morale from the persistent
    // campaign Cohort values (not hardcoded 100). Recovery on the
    // strategic map means a fresh cohort enters battle at 100, but a
    // fatigued survivor of a recent battle enters with whatever it
    // recovered to since its last conclusion.
    stamina: cohort.stamina,
    morale: cohort.morale,
    missileSupply: spec.missileSupply,
    broken: false,
    // v0M4.0b.2: cohorts spawn on-field; exit only on Withdraw-with-Haste
    // edge arrival via phaseCheckExits.
    exited: false,
    // v0M4.0g: XP accumulator resets per battle; battle-conclusion.ts
    // reads it into the Commander xp field at battle close.
    xpEarnedThisBattle: 0,
    figures,
    startSquare: square,
    movedThisRound: false,
    moveDirection: null,
    struckThisRound: false,
    attackedThisRound: false,
    moveOrder: null,
  };
}

// ---------------------------------------------------------------------------
// Formation templates (v0M4.3.4 — xlsx-driven, replaces v0M4.0c hardcoded)
// ---------------------------------------------------------------------------

/**
 * v0M4.3.4 (2026-07-23) — Apply an xlsx-defined formation template to a
 * side. Retires the pre-v0M4.3 hardcoded WideFront / StrongLeft /
 * StrongRight / Square layouts in favour of Michael's BattleMap.xlsx
 * FormationTemplate library (currently: FormationBalanced, FormationWideFront).
 *
 * Algorithm:
 *   1. Sort cohorts by id ascending (creation order = deterministic).
 *   2. For each cohort, resolve its primary tag via `cohortPrimaryTag`:
 *      Siege > Ranged > Cavalry (moveSpeedCombat >= 2) > Melee > Any.
 *   3. Try to fill the lowest-priority same-tag slot that is:
 *      (a) not already filled, AND
 *      (b) not already occupied by another cohort, AND
 *      (c) passable per the terrain grid.
 *   4. If all same-tag slots are exhausted, fall through to the
 *      lowest-priority 'A' (any) slot with the same three constraints.
 *   5. If all 'A' slots also exhausted, spiral-search from the
 *      highest-priority candidate slot's coords for the nearest
 *      passable + unoccupied cell.
 *
 * Mirror rule: side 0 (south) uses the template as-authored (rows 12-16
 * for FormationBalanced). Side 1 (north) reflects through the grid
 * centre — r' = (BATTLESCAPE_HEIGHT - 1) - r, c' = (BATTLESCAPE_WIDTH - 1) - c —
 * so the mirrored formation sits at the opposite pole.
 *
 * `standingOrder` + `moveOrder` are untouched per §2.8.3 — formations
 * only move position + facing.
 */
export function applyFormation(
  side: Side,
  templateName: FormationTemplate,
  terrain?: Uint8Array,
): void {
  const template = FORMATION_TEMPLATES[templateName];
  if (!template) return; // Unknown template name — silent no-op.

  const cohorts = [...side.cohorts.values()]
    .filter((c) => !c.broken && !c.exited)
    .sort((a, b) => (a.id as unknown as number) - (b.id as unknown as number));
  if (cohorts.length === 0) return;

  const zone = sideDeploymentZone(side.sideIndex);

  // Precompute slot lists per tag, sorted ascending by priority.
  const bySide = (s: FormationSlotFromXlsx): Square => mirrorSlotForSide(s, side.sideIndex);
  const tagSlots: Record<FormationSlotTag, FormationSlotFromXlsx[]> = {
    A: slotsByTag(template, 'A'),
    M: slotsByTag(template, 'M'),
    R: slotsByTag(template, 'R'),
    C: slotsByTag(template, 'C'),
    S: slotsByTag(template, 'S'),
  };

  const filledSlotKeys = new Set<string>();
  const occupiedCells = new Set<number>();

  for (const cohort of cohorts) {
    const isCavalry = cohort.stats.moveSpeedCombat >= 2;
    const primaryTag = cohortPrimaryTag({ tags: cohort.tags, isCavalry });

    // Same-tag slots first (in priority order), then 'A' fallback.
    const candidateSlots: FormationSlotFromXlsx[] = [];
    for (const s of tagSlots[primaryTag]) candidateSlots.push(s);
    if (primaryTag !== 'A') {
      for (const s of tagSlots.A) candidateSlots.push(s);
    }

    let placed = false;
    for (const slot of candidateSlots) {
      const slotKey = `${slot.tag}${slot.priority}`;
      if (filledSlotKeys.has(slotKey)) continue;
      const cell = bySide(slot);
      const cellIdx = cell.y * BATTLESCAPE_WIDTH + cell.x;
      if (occupiedCells.has(cellIdx)) continue;
      if (terrain !== undefined && !isPassable(terrainTypeAt(terrain, cell.x, cell.y))) continue;

      filledSlotKeys.add(slotKey);
      occupiedCells.add(cellIdx);
      cohort.position = cell;
      cohort.startSquare = cell;
      cohort.facing = { direction: zone.frontFacing, turnedThisRound: false };
      placed = true;
      break;
    }

    if (!placed) {
      // Spiral-search from the anchor (highest-priority candidate, or
      // side's front-centre if no candidates).
      const anchor: Square = candidateSlots.length > 0
        ? bySide(candidateSlots[0]!)
        : { x: Math.floor(BATTLESCAPE_WIDTH / 2), y: zone.frontY };
      const resolved = spiralSearchForPassable(anchor, occupiedCells, terrain);
      occupiedCells.add(resolved.y * BATTLESCAPE_WIDTH + resolved.x);
      cohort.position = resolved;
      cohort.startSquare = resolved;
      cohort.facing = { direction: zone.frontFacing, turnedThisRound: false };
    }
  }
}

/**
 * Mirror a formation slot to the target side. Side 0 uses coords
 * as-authored; side 1 reflects through the grid centre.
 */
function mirrorSlotForSide(slot: FormationSlotFromXlsx, sideIndex: 0 | 1): Square {
  if (sideIndex === 0) {
    return { x: slot.col, y: slot.row };
  }
  return {
    x: BATTLESCAPE_WIDTH - 1 - slot.col,
    y: BATTLESCAPE_HEIGHT - 1 - slot.row,
  };
}

/**
 * Deterministic outward-spiral neighbour search for the nearest
 * passable + unoccupied cell to `anchor`. Same neighbour-visit order
 * as `resolveTerrainCollision` per BATTLES.md §2.8.6.4.
 */
function spiralSearchForPassable(
  anchor: Square,
  occupied: Set<number>,
  terrain?: Uint8Array,
): Square {
  const OFFSETS: readonly (readonly [number, number])[] = [
    [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1],
  ];
  const cellFree = (x: number, y: number): boolean => {
    if (x < 0 || x >= BATTLESCAPE_WIDTH || y < 0 || y >= BATTLESCAPE_HEIGHT) return false;
    if (occupied.has(y * BATTLESCAPE_WIDTH + x)) return false;
    if (terrain !== undefined && !isPassable(terrainTypeAt(terrain, x, y))) return false;
    return true;
  };
  if (cellFree(anchor.x, anchor.y)) return anchor;
  for (let r = 1; r <= 10; r++) {
    for (const [dx, dy] of OFFSETS) {
      const nx = anchor.x + dx * r;
      const ny = anchor.y + dy * r;
      if (cellFree(nx, ny)) return { x: nx, y: ny };
    }
  }
  return anchor; // Placeholder failure — leaves cohort on the impassable anchor.
}

interface DeploymentZone {
  /** Y-coordinate of the front row (nearest to the enemy). */
  readonly frontY: number;
  /** Direction cohorts on the front row face (toward the enemy). */
  readonly frontFacing: Facing;
  /** ±1 step from the front row toward the rear of the deployment zone. */
  readonly rearwardDy: 1 | -1;
  /** Direction cohorts on the rear row face (away from the enemy). */
  readonly rearFacing: Facing;
}

function sideDeploymentZone(sideIndex: 0 | 1): DeploymentZone {
  if (sideIndex === 0) {
    // Side 0 deploys south (rows 15-19). Front = row 15, facing N.
    // Rear = row 19; rearward step = +1 (moving south from front).
    return { frontY: BATTLESCAPE_HEIGHT - 5, frontFacing: 'N', rearwardDy: 1, rearFacing: 'S' };
  }
  // Side 1 deploys north (rows 0-4). Front = row 4, facing S.
  // Rear = row 0; rearward step = -1.
  return { frontY: 4, frontFacing: 'S', rearwardDy: -1, rearFacing: 'N' };
}

// v0M4.3.4 (2026-07-23) — legacy layout helpers (layoutWideFront,
// layoutStrongFlank, layoutSquare, bucketByTag) removed. Formation
// placement is now xlsx-driven via applyFormation → tag+priority slot
// fill with spiral-search fallback. See applyFormation above.

// ---------------------------------------------------------------------------
// Commander's Orders (v0M4.0d) — army-level cascade per §2.8.2
// ---------------------------------------------------------------------------

/**
 * Cascade a Commander's Order to every cohort on the target side per the
 * `BATTLES.md` §2.8.2 map. Overwrites each cohort's `standingOrder`; per
 * §2.8.5 the player can re-issue individual cohort orders after cascade
 * to override specific cohorts. Cohorts that are `broken` or `exited`
 * are skipped (their state precludes command).
 *
 * v0M4.0d cascade map:
 * - `full-assault`      → melee `advance-and-attack`; ranged `fire-at-will`.
 * - `stand-ground`      → melee `hold-and-defend`;     ranged `hold-fire`.
 * - `cautious-assault`  → front-row melee `advance-and-attack` + front-row
 *                          ranged `fire-at-will`; rear-row melee
 *                          `hold-and-delay` + rear-row ranged `hold-fire`.
 * - `withdraw-with-haste` → all cohorts `withdraw-with-haste` (universal).
 * - `orderly-retreat`   → all cohorts `orderly-retreat` (universal, v0M4.0d
 *                          10th cohort order per §2.8.1.1 amendment).
 *
 * Front vs rear row is determined by the cohort's current `position.y`
 * relative to the side's deployment front row. Any cohort ON the front
 * row (`position.y === frontY`) is "front"; anything else is "rear".
 */
export function cascadeCommanderOrder(side: Side, order: CommanderOrder): void {
  const zone = sideDeploymentZone(side.sideIndex);
  for (const cof of side.cohorts.values()) {
    if (cof.broken || cof.exited) continue;
    cof.standingOrder = mapCommanderOrderToCohortOrder(order, cof, zone);
    // Cascade clears any pending moveOrder so the new order dictates
    // behaviour from the next round. Cohorts that need a moveOrder set
    // (withdraw-with-haste, orderly-retreat, flank-*) will have it
    // repopulated by phaseBattleAI on the next round.
    cof.moveOrder = null;
  }
}

function mapCommanderOrderToCohortOrder(
  order: CommanderOrder,
  cof: CohortOnField,
  zone: DeploymentZone,
): StandingOrder {
  const isRangedOnly = cof.tags.includes('Ranged') && !cof.tags.includes('Melee');
  switch (order) {
    case 'full-assault':
      return isRangedOnly ? 'fire-at-will' : 'advance-and-attack';
    case 'stand-ground':
      return isRangedOnly ? 'hold-fire' : 'hold-and-defend';
    case 'cautious-assault': {
      const isFrontRow = cof.position.y === zone.frontY;
      if (isFrontRow) {
        return isRangedOnly ? 'fire-at-will' : 'advance-and-attack';
      }
      return isRangedOnly ? 'hold-fire' : 'hold-and-delay';
    }
    case 'withdraw-with-haste':
      return 'withdraw-with-haste';
    case 'orderly-retreat':
      return 'orderly-retreat';
  }
}
